/**
 * SkillsPage Component
 *
 * Listagem de Skills (workflows com usageType='skill'). Segue o padrão-lista do
 * Design System (referência: UsersPage): toolbar 3 zonas + filterbar de chips +
 * bulk bar + view-toggle Lista/Cards (mobile = cards). Mesmas funções da versão
 * anterior — só o shell foi reestruturado.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useWorkflows,
  useDeleteWorkflow,
  useActivateWorkflow,
  usePauseWorkflow,
  useDuplicateWorkflow,
  useUpdateWorkflow,
  useRootWorkflowFolders,
  useWorkflowSubfolders,
  useWorkflowFolder,
  useWorkflowFolderAncestors,
  useCreateWorkflowFolder,
  useUpdateWorkflowFolder,
  useDeleteWorkflowFolder,
} from '@/modules/workflows/hooks';
import { FolderList, CreateFolderModal } from '@/modules/workflows/components';
import { ImportWorkflowModal } from '@/modules/workflows/components/import/ImportWorkflowModal';
import { workflowsApi } from '@/modules/workflows/services';
import { downloadJson, slugifyFilename } from '@/modules/workflows/utils/workflowTransfer';
import type {
  WorkflowResponse,
  WorkflowStatus,
  WorkflowQuery,
} from '@/modules/workflows/types';
import type { WorkflowFolderResponse, CreateWorkflowFolderRequest } from '@troia-v3/types';
import { Button, FiltersButton, FilterBottomSheet, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { SkillCard } from '../components/SkillCard';
import { SkillListItem } from '../components/SkillListItem';

type SortKey = 'name' | 'status' | 'updatedAt';
type ViewMode = 'grid' | 'table';

const STATUS_FILTERS: ReadonlyArray<readonly [WorkflowStatus | '', string]> = [
  ['', 'Todos'],
  ['active', 'Ativo'],
  ['inactive', 'Pausado'],
  ['draft', 'Rascunho'],
  ['archived', 'Arquivado'],
];

const STATUS_RANK: Record<WorkflowStatus, number> = {
  active: 0,
  inactive: 1,
  draft: 2,
  archived: 3,
};

const STATUS_LABEL: Record<WorkflowStatus, string> = {
  active: 'Ativo',
  inactive: 'Pausado',
  draft: 'Rascunho',
  archived: 'Arquivado',
};

export const SkillsPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | ''>('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'updatedAt', dir: 'desc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const limit = 12;

  // ── Pastas (R27: skills SÃO workflows — mesma folderbar/pastas da tela de Workflows) ──
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<WorkflowFolderResponse | null>(null);

  // Árvore de pastas de SKILLS (usageType 'skill') — separada da de Workflows
  const { data: rootFolders, isLoading: foldersLoading } = useRootWorkflowFolders('skill');
  const { data: subfolders } = useWorkflowSubfolders(currentFolderId || undefined, 'skill');
  const { data: currentFolder } = useWorkflowFolder(currentFolderId || undefined);
  const { data: ancestors } = useWorkflowFolderAncestors(currentFolderId);

  const createFolder = useCreateWorkflowFolder();
  const updateFolder = useUpdateWorkflowFolder();
  const deleteFolder = useDeleteWorkflowFolder();

  const breadcrumb = useMemo(() => {
    if (!currentFolder) return [];
    return [...(ancestors || []), currentFolder];
  }, [currentFolder, ancestors]);

  const displayFolders = useMemo(() => {
    if (currentFolderId && subfolders) return subfolders;
    return rootFolders || [];
  }, [currentFolderId, subfolders, rootFolders]);

  const query: WorkflowQuery = useMemo(
    () => ({
      page,
      limit,
      search: search || undefined,
      status: statusFilter || undefined,
      usageType: 'skill',
      folderId: currentFolderId === null ? 'null' : currentFolderId,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
    }),
    [page, search, statusFilter, currentFolderId]
  );

  const { data, isLoading, error } = useWorkflows(query);

  const deleteWorkflow = useDeleteWorkflow();
  const activateWorkflow = useActivateWorkflow();
  const pauseWorkflow = usePauseWorkflow();
  const duplicateWorkflow = useDuplicateWorkflow();
  const updateWorkflow = useUpdateWorkflow();

  // ── Handlers de pasta (mesmo contrato da tela de Workflows) ──
  const handleSelectFolder = useCallback((id: string | null) => {
    setCurrentFolderId(id);
    setPage(1);
  }, []);

  const handleCreateFolder = useCallback(() => {
    setEditingFolder(null);
    setFolderModalOpen(true);
  }, []);

  const handleEditFolder = useCallback((folder: WorkflowFolderResponse) => {
    setEditingFolder(folder);
    setFolderModalOpen(true);
  }, []);

  const handleDeleteFolder = useCallback(async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja excluir esta pasta? Os workflows dentro dela serão movidos para a raiz.', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      try {
        await deleteFolder.mutateAsync({ id, force: true });
        if (currentFolderId === id) setCurrentFolderId(null);
      } catch (err) {
        console.error('Error deleting folder:', err);
      }
    }
  }, [deleteFolder, currentFolderId, confirm]);

  const handleFolderSubmit = useCallback(async (formData: CreateWorkflowFolderRequest) => {
    if (editingFolder) {
      await updateFolder.mutateAsync({
        id: editingFolder.id,
        data: { name: formData.name, description: formData.description, color: formData.color },
      });
    } else {
      await createFolder.mutateAsync({ ...formData, usageType: 'skill', parentId: currentFolderId || undefined });
    }
  }, [editingFolder, updateFolder, createFolder, currentFolderId]);

  const handleDropWorkflow = useCallback(async (workflowId: string, targetFolderId: string) => {
    try {
      await updateWorkflow.mutateAsync({ id: workflowId, data: { folderId: targetFolderId } });
    } catch (err) {
      console.error('Error moving skill to folder:', err);
    }
  }, [updateWorkflow]);

  const handleDropWorkflowToRoot = useCallback(async (workflowId: string) => {
    try {
      await updateWorkflow.mutateAsync({ id: workflowId, data: { folderId: null } });
    } catch (err) {
      console.error('Error moving skill to root:', err);
    }
  }, [updateWorkflow]);

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (a query já pede updatedAt desc no backend).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'status') cmp = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      else cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((s) => selectedIds.has(s.id));
  const someOnPageSelected = items.some((s) => selectedIds.has(s.id));

  const handleSearch = (term: string) => {
    setSearch(term);
    setPage(1);
  };

  const applyStatusFilter = (status: WorkflowStatus | '') => {
    setStatusFilter(status);
    setPage(1);
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setStatusFilter('');
    setPage(1);
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((s) => next.delete(s.id));
      else items.forEach((s) => next.add(s.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleCreateNew = useCallback(() => {
    navigate('/automation/skills/new');
  }, [navigate]);

  // "+" central do bottom-nav mobile → nova skill (mockup .m-fab).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); handleCreateNew(); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [handleCreateNew]);

  const handleEdit = useCallback(
    (id: string) => {
      navigate(`/automation/skills/${id}`);
    },
    [navigate]
  );

  const handleDuplicate = async (id: string) => {
    try {
      const skill = items.find((w) => w.id === id);
      await duplicateWorkflow.mutateAsync({
        id,
        newName: skill ? `${skill.name} (Cópia)` : undefined,
      });
    } catch (err) {
      console.error('Error duplicating skill:', err);
    }
  };

  // Export/Import — mesmo motor de portabilidade dos Workflows (skills SÃO
  // workflows usageType='skill'); o exportAll('skill') restringe o bundle.
  const handleExportAll = async () => {
    try {
      const file = await workflowsApi.exportAll('skill');
      if (file.workflows.length === 0) {
        toast.info('Nenhuma skill para exportar.');
        return;
      }
      downloadJson('skills.json', file);
    } catch (err) {
      console.error('Error exporting skills:', err);
      toast.error('Falha ao exportar skills.');
    }
  };

  const handleExportOne = async (id: string) => {
    try {
      const file = await workflowsApi.exportOne(id);
      const name = file.workflows[0]?.name ?? 'skill';
      downloadJson(slugifyFilename(name), file);
    } catch (err) {
      console.error('Error exporting skill:', err);
      toast.error('Falha ao exportar a skill.');
    }
  };

  const handleDelete = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja excluir este skill?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      try {
        await deleteWorkflow.mutateAsync(id);
      } catch (err) {
        console.error('Error deleting skill:', err);
      }
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await activateWorkflow.mutateAsync(id);
    } catch (err) {
      console.error('Error activating skill:', err);
    }
  };

  const handlePause = async (id: string) => {
    try {
      await pauseWorkflow.mutateAsync(id);
    } catch (err) {
      console.error('Error pausing skill:', err);
    }
  };

  // Bulk (mutations single em loop, mirror da UsersPage).
  const bulkPause = () => {
    items
      .filter((s) => selectedIds.has(s.id) && s.status === 'active')
      .forEach((s) => pauseWorkflow.mutate(s.id));
    clearSelection();
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} skill(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteWorkflow.mutate(id));
    clearSelection();
  };

  const statusLabel = statusFilter ? STATUS_LABEL[statusFilter] : null;
  const activeFilterCount = statusLabel ? 1 : 0;

  const cardHandlers = {
    onToggleSelect: toggleSelect,
    onEdit: handleEdit,
    onDuplicate: handleDuplicate,
    onExport: handleExportOne,
    onDelete: handleDelete,
    onActivate: handleActivate,
    onPause: handlePause,
  } as const;

  const renderCheck = (on: boolean, mixed = false, onClick?: () => void, label?: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid="skills-select-all"
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  const SortHead: React.FC<{ label: string; k: SortKey; className?: string }> = ({ label, k, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none ${className ?? ''}`}
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
        Status
      </p>
      {STATUS_FILTERS.map(([val, label]) => {
        const active = statusFilter === val;
        return (
          <button
            key={label}
            onClick={() => applyStatusFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={`skills-filter-status-${val || 'all'}`}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          /* Esquerda: breadcrumb + contador */
          <>
          <nav className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/automation')}
              className="text-sm font-medium truncate hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="skills-breadcrumb-automation"
            >
              Automação
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>
              Skills
            </span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-F22: ToolbarSearch, 360px) */
          <ToolbarSearch
            value={search}
            onChange={handleSearch}
            placeholder="Buscar skill…"
            shrinkClass="ds-search"
            data-testid="skills-search"
          />
        }
        right={
          /* Direita (ordem canônica): Filtros → Toggle → overflow → separador → CTA */
          <>
          {/* Filtros (F05: FiltersButton shared) */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="skills-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="skills-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-F11: Lista primeiro, grid = "Cards") */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="skills-view"
          />

          {/* Import / Export tudo (overflow) — paridade com a tela de Workflows */}
          <div className="flex-none">
            <CardActionsMenu
              size="md"
              data-testid="skills-more-actions"
              actions={[
                { label: 'Importar', icon: 'upload', onClick: () => setImportOpen(true) },
                { label: 'Exportar tudo', icon: 'download', onClick: handleExportAll },
              ]}
            />
          </div>

          <span className="w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova skill (único primário — F01-F03: Button shared, rótulo colapsa via @container) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={handleCreateNew}
            data-testid="new-skill"
          >
            Nova skill
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={search}
              onChange={handleSearch}
              placeholder="Buscar skill…"
              data-testid="skills-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="skills-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Pastas (`.folderbar` — mesma barra da tela de Workflows, R27) — acima da filterbar/bulk (mockup) ── */}
      <FolderList
        folders={displayFolders}
        currentFolder={currentFolder}
        breadcrumb={breadcrumb}
        isLoading={foldersLoading}
        onSelectFolder={handleSelectFolder}
        onCreateFolder={handleCreateFolder}
        onEditFolder={handleEditFolder}
        onDeleteFolder={handleDeleteFolder}
        onDropWorkflow={handleDropWorkflow}
        onDropWorkflowToRoot={handleDropWorkflowToRoot}
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div
          className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
          style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}
        >
          {statusLabel && (
            <span
              className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
            >
              Status: {statusLabel}
              <button
                onClick={() => applyStatusFilter('')}
                className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100"
                aria-label="Remover filtro de status"
                data-testid="skills-chip-remove-status"
              >
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="skills-clear-filters">
            Limpar tudo
          </button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div
          className="flex-none flex items-center gap-2.5 px-4 py-2"
          style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}
        >
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>
            {selectedCount} selecionado(s)
          </span>
          <span className="flex-1" />
          <button
            onClick={bulkPause}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold"
            style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
            data-testid="skills-bulk-pause"
          >
            <Icon name="pause" className="w-3.5 h-3.5" /> Pausar
          </button>
          <button
            onClick={bulkDelete}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}
            data-testid="skills-bulk-delete"
          >
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="skills-bulk-clear">
            Cancelar
          </button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar skills: {(error as Error).message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando skills…" />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="zap"
              message="Nenhum skill encontrado"
              description={
                search || activeFilterCount > 0
                  ? 'Tente ajustar a busca ou os filtros.'
                  : 'Skills são workflows invocáveis por agentes de IA. Crie seu primeiro skill para expandir as capacidades dos seus agentes.'
              }
              actionLabel="Nova skill"
              onAction={handleCreateNew}
            />
          )}

          {/* Grid (Cards) */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
              {sortedItems.map((skill: WorkflowResponse) => (
                <SkillCard key={skill.id} skill={skill} {...cardHandlers} />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((skill: WorkflowResponse) => (
                  <SkillCard key={skill.id} skill={skill} {...cardHandlers} />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="pl-4 pr-1 py-3 w-11">
                        {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos')}
                      </th>
                      <SortHead label="Skill" k="name" />
                      <SortHead label="Status" k="status" />
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Modo</th>
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Etapas</th>
                      <SortHead label="Atualizado" k="updatedAt" />
                      <th className="px-4 py-3 w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((skill: WorkflowResponse) => (
                      <SkillListItem
                        key={skill.id}
                        skill={skill}
                        selected={selectedIds.has(skill.id)}
                        {...cardHandlers}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={(p) => setPage(p)}
              totalItems={data.total}
              pageSize={limit}
              itemLabel="skills"
            />
          )}
        </div>
      </div>

      {/* Import Skill Modal — mesmo wizard 2 passos dos Workflows */}
      {importOpen && (
        <ImportWorkflowModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          currentFolderId={currentFolderId}
          entity="skill"
        />
      )}

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="skills-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      {/* Criar/editar pasta (compartilhado com Workflows) */}
      <CreateFolderModal
        isOpen={folderModalOpen}
        onClose={() => {
          setFolderModalOpen(false);
          setEditingFolder(null);
        }}
        onSubmit={handleFolderSubmit}
        folder={editingFolder}
        parentId={currentFolderId}
      />
    </div>
  );
};

export default SkillsPage;
